import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import HubProducts from '../components/HubProducts';
import VendorCreditReport from '../components/VendorCreditReport';
import EducationModuleManager from '../components/EducationModuleManager';
import Accordion from '../components/Accordion';
import { useSearchParams } from 'react-router-dom';
import ConsignmentProductManager from '../components/ConsignmentProductManager';
import { VendorFavoritesDashboard } from '../system-modules/favorites/components';
import BuyerInbox from '../system-modules/favorites/components/BuyerInbox';
import PaymentSetup from '../components/PaymentSetup';

export default function HubDashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCommission, setInviteCommission] = useState(10);
  const [inviting, setInviting] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [farmerProducts, setFarmerProducts] = useState([]);
  const [updatingCommission, setUpdatingCommission] = useState(false);
  const [creditSettings, setCreditSettings] = useState({ enabled: false, percentage: 5 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [storefront, setStorefront] = useState({
    bannerImage: '',
    primaryColor: '#16a34a',
    secondaryColor: '#15803d',
    layoutStyle: 'grid',
    showTeam: true,
    customMessage: '',
    teamMembers: [],
    overlayText: true
  });
  const [savingStorefront, setSavingStorefront] = useState(false);
  const [educationModules, setEducationModules] = useState([]);
  const [loadingEducation, setLoadingEducation] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  useEffect(() => {
    fetchDashboard();
    fetchCreditSettings();
    fetchStorefrontSettings();
    fetchEducationModules();
    fetchUnreadCount();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/hub/dashboard', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditSettings = async () => {
    try {
      const res = await fetch('/api/credit-settings/settings', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCreditSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch credit settings:', err);
    }
  };

  const fetchStorefrontSettings = async () => {
    if (!user?._id) return;
    try {
      const res = await fetch(`/api/users/storefront/${user._id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.storefront) {
          setStorefront(prev => ({ ...prev, ...data.storefront }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch storefront settings:', err);
    }
  };

  const fetchEducationModules = async () => {
    setLoadingEducation(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/education-modules', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEducationModules(data);
      }
    } catch (err) {
      console.error('Failed to fetch education modules:', err);
    } finally {
      setLoadingEducation(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch('/api/favorites/communication/unread/count', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const saveCreditSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch('/api/credit-settings/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(creditSettings)
      });
      if (res.ok) {
        alert('Credit settings saved successfully');
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to save settings');
      }
    } catch (err) {
      alert('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveStorefront = async () => {
    setSavingStorefront(true);
    try {
      const payload = {
        bannerImage: storefront.bannerImage,
        primaryColor: storefront.primaryColor,
        secondaryColor: storefront.secondaryColor,
        layoutStyle: storefront.layoutStyle,
        showTeam: storefront.showTeam,
        customMessage: storefront.customMessage,
        teamMembers: storefront.teamMembers || [],
        overlayText: storefront.overlayText
      };
      
      const res = await fetch('/api/users/storefront', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert('Storefront settings saved');
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Error saving settings');
    } finally {
      setSavingStorefront(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await fetch('/api/hub/invite-farmer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ farmerEmail: inviteEmail, defaultCommissionRate: inviteCommission })
      });
      if (!res.ok) throw new Error(await res.text());
      alert('Invitation sent successfully!');
      setInviteEmail('');
      setInviteCommission(10);
      setShowInviteModal(false);
      fetchDashboard();
    } catch (error) {
      alert(error.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

const openProductsModal = async (supplier) => {
  setSelectedFarmer(supplier);
  setShowProductsModal(true);
  setFarmerProducts([]);
  setLoadingProducts(true);
  
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/hub/farmer-products/${supplier.farmerId._id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    setFarmerProducts(data);
  } catch (error) {
    console.error('Failed to fetch farmer products:', error);
    alert('Could not load products');
  } finally {
    setLoadingProducts(false);
  }
};

  const updateCommissionAndOverrides = async (productId, commission, price, description, inventory) => {
    setUpdatingCommission(true);
    try {
      const body = { farmerId: selectedFarmer.farmerId._id, productId };
      if (commission !== undefined) body.commissionRate = commission === '' ? null : parseFloat(commission);
      if (price !== undefined) body.price = price === '' ? null : parseFloat(price);
      if (description !== undefined) body.description = description === '' ? null : description;
      if (inventory !== undefined) body.inventory = inventory === '' ? null : parseInt(inventory);
      const res = await fetch('/api/hub/update-commission', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(await res.text());
      const fresh = await fetch(`/api/hub/farmer-products/${selectedFarmer.farmerId._id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await fresh.json();
      setFarmerProducts(data);
      alert('Commission and overrides updated');
    } catch (error) {
      console.error('Failed to update:', error);
      alert('Error updating');
    } finally {
      setUpdatingCommission(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading dashboard...</div>;
  if (!dashboard) return <div className="text-center py-8">Unable to load dashboard</div>;

  const suppliers = dashboard.suppliers || [];
  const activeSuppliers = suppliers.filter(s => s.status === 'active');
  const pendingSuppliers = suppliers.filter(s => s.status === 'pending');

  const accordionItems = [
    {
      icon: '📦',
      title: 'My Products',
      badge: 'Manage',
      content: <HubProducts />
    },
    {
      icon: '🤝',
      title: `Hub Producers (${activeSuppliers.length})`,
      badge: activeSuppliers.length,
      content: (
        <div>
          <div className="mb-4 flex justify-end">
            <button onClick={() => setShowInviteModal(true)} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
              + Invite Farmer
            </button>
          </div>
          {activeSuppliers.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No active farmers yet.</p>
          ) : (
            <div className="space-y-3">
              {activeSuppliers.map(supplier => (
                <div key={supplier._id} className="border rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{supplier.farmerId?.farmName || supplier.farmerId?.name}</div>
                    <div className="text-sm text-gray-500">{supplier.farmerId?.email}</div>
                    <div className="text-xs text-gray-400">Commission: {supplier.defaultCommissionRate || dashboard.hub?.commissionRate}%</div>
                  </div>
                  <button onClick={() => openProductsModal(supplier)} className="text-blue-600 hover:text-blue-800 text-sm">
                    ⚙️ Products
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      icon: '📨',
      title: `Pending Invites (${pendingSuppliers.length})`,
      badge: pendingSuppliers.length,
      content: (
        <div>
          {pendingSuppliers.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No pending invitations.</p>
          ) : (
            <div className="space-y-3">
              {pendingSuppliers.map(supplier => (
                <div key={supplier._id} className="border rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{supplier.farmerId?.farmName || supplier.farmerId?.name}</div>
                    <div className="text-sm text-gray-500">{supplier.farmerId?.email}</div>
                  </div>
                  <div className="text-sm text-yellow-600">Awaiting response</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      icon: '💰',
      title: 'Credit Settings',
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold">Buyer Credit Program</h3>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={creditSettings.enabled}
              onChange={(e) => setCreditSettings({ ...creditSettings, enabled: e.target.checked })}
              className="w-4 h-4"
            />
            <span>Enable buyer credits for your hub</span>
          </label>
          {creditSettings.enabled && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Credit Percentage ({creditSettings.percentage}%)
              </label>
              <input
                type="range"
                min="1"
                max="25"
                step="1"
                value={creditSettings.percentage}
                onChange={(e) => setCreditSettings({ ...creditSettings, percentage: parseInt(e.target.value) })}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Buyers will earn {creditSettings.percentage}% back in credits on every purchase.
              </p>
            </div>
          )}
          <button
            onClick={saveCreditSettings}
            disabled={savingSettings}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )
    },
    {
      icon: '📊',
      title: 'Credit Reports',
      content: <VendorCreditReport type="hub" title="Credit Activity" />
    },
    {
      icon: '💳',
      title: 'Payment Setup',
      badge: 'Stripe',
      content: <PaymentSetup />
    },
    {
      icon: '❤️',
      title: 'Favorites',
      badge: 'Stats',
      content: <VendorFavoritesDashboard />
    },
    {
      icon: '📬',
      title: 'Inbox',
      badge: unreadCount > 0 ? `${unreadCount} new` : 'Messages',
      content: <BuyerInbox />
    },
    {
      icon: '🏪',
      title: 'Storefront',
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold">Customize Your Hub Storefront</h3>
          <div>
            <label className="block text-sm font-medium mb-1">Banner Image</label>
            <input 
              type="file" 
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const res = await fetch('/api/upload/storefront', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    body: formData
                  });
                  const data = await res.json();
                  if (data.url) {
                    setStorefront({ ...storefront, bannerImage: data.url });
                    alert('Banner uploaded');
                  }
                } catch (err) {
                  alert('Upload failed');
                }
              }}
              className="w-full border rounded px-3 py-2"
            />
            {storefront.bannerImage && (
              <div className="mt-2">
                <img src={storefront.bannerImage} alt="Banner" className="h-32 object-cover rounded" />
                <button onClick={() => setStorefront({ ...storefront, bannerImage: '' })} className="text-red-500 text-sm mt-1">
                  Remove
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Primary Color</label>
            <input 
              type="color" 
              value={storefront.primaryColor}
              onChange={(e) => setStorefront({ ...storefront, primaryColor: e.target.value })}
              className="w-12 h-12 border rounded cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Secondary Color</label>
            <input 
              type="color" 
              value={storefront.secondaryColor}
              onChange={(e) => setStorefront({ ...storefront, secondaryColor: e.target.value })}
              className="w-12 h-12 border rounded cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Custom Message</label>
            <textarea 
              value={storefront.customMessage}
              onChange={(e) => setStorefront({ ...storefront, customMessage: e.target.value })}
              rows="3"
              className="w-full border rounded px-3 py-2"
              placeholder="Welcome to our hub!"
            />
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={storefront.showTeam}
              onChange={(e) => setStorefront({ ...storefront, showTeam: e.target.checked })}
              className="w-4 h-4"
            />
            <span>Show team section</span>
          </div>
          {storefront.showTeam && (
            <div>
              <h4 className="font-medium mb-2">Team Members</h4>
              {storefront.teamMembers?.map((member, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input 
                    type="text" 
                    placeholder="Name" 
                    value={member.name}
                    onChange={(e) => {
                      const newTeam = [...(storefront.teamMembers || [])];
                      newTeam[idx].name = e.target.value;
                      setStorefront({ ...storefront, teamMembers: newTeam });
                    }}
                    className="flex-1 border rounded px-2 py-1"
                  />
                  <input 
                    type="text" 
                    placeholder="Role" 
                    value={member.role}
                    onChange={(e) => {
                      const newTeam = [...(storefront.teamMembers || [])];
                      newTeam[idx].role = e.target.value;
                      setStorefront({ ...storefront, teamMembers: newTeam });
                    }}
                    className="flex-1 border rounded px-2 py-1"
                  />
                  <button 
                    onClick={() => {
                      const newTeam = storefront.teamMembers.filter((_, i) => i !== idx);
                      setStorefront({ ...storefront, teamMembers: newTeam });
                    }} 
                    className="text-red-500 px-2"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button 
                onClick={() => setStorefront({ 
                  ...storefront, 
                  teamMembers: [...(storefront.teamMembers || []), { name: '', role: '' }] 
                })} 
                className="text-green-600 text-sm mt-2"
              >
                + Add Team Member
              </button>
            </div>
          )}
          <button
            onClick={saveStorefront}
            disabled={savingStorefront}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full"
          >
            {savingStorefront ? 'Saving...' : 'Save Storefront Settings'}
          </button>
        </div>
      )
    },
    {
      icon: '📚',
      title: 'Education',
      badge: educationModules.length || 0,
      content: (
        <EducationModuleManager 
          role="hub"
          modules={educationModules}
          onRefresh={fetchEducationModules}
          loading={loadingEducation}
        />
      )
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Hub Dashboard</h1>
        {user?.slug && (
          <Link to={`/hub/${user.slug}`} target="_blank" className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700">
            View Storefront
          </Link>
        )}
      </div>
      {user?.slug && (
        <div className="text-sm text-gray-600 mb-4">
          URL: <code className="bg-gray-100 px-2 py-1 rounded">ourpickup.com/hub/{user.slug}</code>
          <button onClick={() => navigator.clipboard.writeText(`https://ourpickup.com/hub/${user.slug}`)} className="ml-2 text-blue-600 text-xs">Copy</button>
        </div>
      )}
      
      <Accordion 
        items={accordionItems} 
        defaultOpen={0}
        className="mt-4"
        headerClassName="text-base font-medium py-4"
        iconOpen="▼"
        iconClosed="▶"
      />

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Invite a Farmer</h2>
            <form onSubmit={handleInvite}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Farmer's Email</label>
                <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="w-full border rounded-md px-3 py-2" required />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Default Commission Rate (%)</label>
                <input type="number" min="0" max="100" step="0.5" value={inviteCommission} onChange={(e) => setInviteCommission(parseFloat(e.target.value))} className="w-full border rounded-md px-3 py-2" required />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowInviteModal(false)} className="px-4 py-2 border rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={inviting} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400">
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

{/* Products Modal - Consignment Products Management */}
{showProductsModal && selectedFarmer && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">
          Consignment Products - {selectedFarmer.farmerId?.farmName || selectedFarmer.farmerId?.name}
        </h2>
        <button
          onClick={() => {
            setShowProductsModal(false);
            setSelectedFarmer(null);
            setFarmerProducts([]);
          }}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>
      
      {loadingProducts ? (
        <div className="text-center py-8 text-gray-500">Loading products...</div>
      ) : farmerProducts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No products found for this farmer.</div>
      ) : (
        <ConsignmentProductManager
          farmerId={selectedFarmer.farmerId._id}
          farmerName={selectedFarmer.farmerId?.farmName || selectedFarmer.farmerId?.name}
          products={farmerProducts}
          onUpdate={() => {
            fetchDashboard();
            openProductsModal(selectedFarmer);
          }}
          onClose={() => {
            setShowProductsModal(false);
            setSelectedFarmer(null);
            setFarmerProducts([]);
          }}
        />
      )}
    </div>
  </div>
)}
    </div>
  );
}
